import { useEffect, useMemo, useState } from "react";
import { ErrorBoundary } from "./components/common/ErrorBoundary";
import { AppShell, type ViewId } from "./components/layout/AppShell";
import { AccessAdminPage } from "./pages/AccessAdminPage";
import { AboutPage } from "./pages/AboutPage";
import { DashboardHome } from "./pages/DashboardHome";
import { LandingPage } from "./pages/LandingPage";
import { HealthMapPage } from "./pages/HealthMapPage";
import { JobDetailPage } from "./pages/JobDetailPage";
import { MarketingPage } from "./pages/MarketingPage";
import { BusinessAnalysisPage } from "./pages/BusinessAnalysisPage";
import { ProfitLossPage } from "./pages/ProfitLossPage";
import { ReportsPage } from "./pages/ReportsPage";
import { OrderPlanningPage } from "./pages/OrderPlanningPage";
import { GasPricesPage } from "./pages/reports/GasPricesPage";
import { RunsMonitoringPage } from "./pages/RunsMonitoringPage";
import { SettingsJobsPage } from "./pages/SettingsJobsPage";
import { StationAnalyticsPage } from "./pages/StationAnalyticsPage";
import { useAuthUser } from "./auth/AuthProvider";
import { subscribeApiStatus, type ApiStatus } from "./services/apiClient";
import { refreshRunHistory, type RefreshRunsSummary } from "./services/refreshService";
import { trackPageView } from "./services/telemetry";
import "./styles/app.css";

function buildRefreshWarnings(summary: RefreshRunsSummary): string[] {
  const warnings: string[] = [];

  if (summary.powerBi?.workspacesChecked === 0) {
    warnings.push("Power BI monitoring did not discover any workspaces for the Function credential.");
  }

  if (summary.powerBi?.errors.length) {
    warnings.push(...summary.powerBi.errors.map((error) => error.message));
  }

  if (summary.sqlEtl?.errors.length) {
    warnings.push(...summary.sqlEtl.errors.map((error) => `SQL ETL: ${error.message}`));
  }

  return [...new Set(warnings)];
}

function App() {
  const { portalContext } = useAuthUser();
  const [activeView, setActiveView] = useState<ViewId>("landing");
  const [selectedStationSk, setSelectedStationSk] = useState<number>();
  const [selectedJobId, setSelectedJobId] = useState<string>();
  const [refreshKey, setRefreshKey] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(new Date().toLocaleString());
  const [apiStatus, setApiStatus] = useState<ApiStatus>({
    apiAvailable: true,
    dataMode: "mock",
    message: "Mock mode is active."
  });
  const telemetryUserKey = portalContext?.userPrincipalName || portalContext?.email || portalContext?.entraObjectId;

  useEffect(() => subscribeApiStatus(setApiStatus), []);

  useEffect(() => {
    if (!telemetryUserKey) {
      return;
    }

    trackPageView(`DataOps ${activeView}`, `${window.location.origin}${window.location.pathname}#${activeView}`);
  }, [activeView, telemetryUserKey]);

  useEffect(() => {
    const healthViews: ViewId[] = ["dashboard", "runs", "job-detail", "health-map", "settings"];
    if (healthViews.includes(activeView) && portalContext && !portalContext.features.healthMonitoring) {
      setSelectedJobId(undefined);
      setActiveView("landing");
    }
    if (activeView === "access-admin" && portalContext && !portalContext.features.stationAccessAdmin) {
      setActiveView("landing");
    }
    if ((activeView === "reports" || activeView === "reports-bistro" || activeView === "reports-order-planning") && portalContext && !portalContext.features.reports) {
      setActiveView("landing");
    }
    if (activeView === "profit-loss" && portalContext && !portalContext.features.profitLoss) {
      setActiveView("landing");
    }
    if (activeView === "business-analysis" && portalContext && !portalContext.features.businessAnalysis) {
      setActiveView("landing");
    }
    if ((activeView === "marketing" || activeView === "marketing-station-sns") && portalContext && !portalContext.features.marketingAnalytics) {
      setActiveView("landing");
    }
    if (activeView === "settings" && portalContext && !(portalContext.features.healthAdmin || portalContext.features.portalExecutiveMember)) {
      setActiveView("landing");
    }
  }, [activeView, portalContext]);

  useEffect(() => {
    if (!portalContext || selectedStationSk === undefined) {
      return;
    }

    const hasStationAccess = portalContext.stations.some((station) => station.stationSk === selectedStationSk);
    if (!hasStationAccess) {
      setSelectedStationSk(undefined);
    }
  }, [portalContext, selectedStationSk]);

  function openJob(jobId: string) {
    setSelectedJobId(jobId);
    setActiveView("job-detail");
  }

  async function refresh() {
    if (isRefreshing) {
      return;
    }

    setIsRefreshing(true);
    try {
      const summary = await refreshRunHistory();
      const warnings = buildRefreshWarnings(summary);
      setRefreshKey((value) => value + 1);
      setLastUpdatedAt(new Date().toLocaleString());
      if (warnings.length) {
        setApiStatus((current) => ({
          apiAvailable: false,
          dataMode: current.dataMode === "unknown" ? "sql" : current.dataMode,
          message: warnings.join(" ")
        }));
      }
    } catch (error) {
      setApiStatus((current) => ({
        apiAvailable: false,
        dataMode: current.dataMode,
        message: error instanceof Error ? error.message : "Run history refresh failed."
      }));
    } finally {
      setIsRefreshing(false);
    }
  }

  function openStationAnalytics(stationSk?: number) {
    setSelectedStationSk(stationSk);
    setActiveView("station-analytics");
  }

  const page = useMemo(() => {
    switch (activeView) {
      case "landing":
        return <LandingPage onNavigate={setActiveView} />;
      case "runs":
        return <RunsMonitoringPage refreshKey={refreshKey} onOpenJob={openJob} />;
      case "job-detail":
        return <JobDetailPage jobId={selectedJobId} refreshKey={refreshKey} onBack={() => setActiveView("runs")} />;
      case "health-map":
        return <HealthMapPage refreshKey={refreshKey} onOpenJob={openJob} />;
      case "settings":
        return <SettingsJobsPage refreshKey={refreshKey} />;
      case "about":
        return <AboutPage />;
      case "station-analytics":
        return <StationAnalyticsPage selectedStationSk={selectedStationSk} />;
      case "reports":
      case "reports-bistro":
        return <ReportsPage />;
      case "reports-order-planning":
        return <OrderPlanningPage />;
      case "reports-gas-prices":
        return <GasPricesPage />;
      case "profit-loss":
        return <ProfitLossPage />;
      case "business-analysis":
        return <BusinessAnalysisPage />;
      case "marketing":
        return <MarketingPage view="overview" />;
      case "marketing-station-sns":
        return <MarketingPage view="station-sns" />;
      case "access-admin":
        return <AccessAdminPage />;
      default:
        return <DashboardHome refreshKey={refreshKey} onOpenJob={openJob} onOpenRuns={() => setActiveView("runs")} />;
    }
  }, [activeView, refreshKey, selectedJobId, selectedStationSk]);

  return (
    <AppShell
      activeView={activeView}
      apiStatus={apiStatus}
      isRefreshing={isRefreshing}
      lastUpdatedAt={lastUpdatedAt}
      selectedStationSk={selectedStationSk}
      onNavigate={setActiveView}
      onNavigateStation={openStationAnalytics}
      onRefresh={refresh}
      onNavigateLanding={() => setActiveView("landing")}
    >
      <ErrorBoundary resetKey={`${activeView}-${refreshKey}`}>
        {page}
      </ErrorBoundary>
    </AppShell>
  );
}

export default App;
